// src/services/FetchUserData.js
export async function FetchUserData(userId) {
  if (!userId) throw new Error('Missing userId');

  try {
    // Ensure correct endpoint name (lowercase) and allow REACT_APP_API_URL override
    const apiBase = process.env.REACT_APP_API_URL || 'https://www.xfactual.org/api';
    const apiUrl = `${apiBase.replace(/\/$/, '')}/getuserdata.php`; // <-- getuserdata.php (lowercase)

    const url = `${apiUrl}?user_id=${encodeURIComponent(userId)}`;

    // add a timeout so we don't hang forever (30s)
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 100000);

    const resp = await fetch(url, { signal: controller.signal });
    clearTimeout(timeout);

    if (!resp.ok) {
      throw new Error(`Error fetching user data: ${resp.status} ${resp.statusText}`);
    }

    const data = await resp.json();
    console.log('Raw API response (FetchUserData):', data);

    const makePreview = (full = '') => {
      const s = String(full ?? '').trim();
      if (!s) return '';
      const max = 80;
      return s.length > max ? s.slice(0, max).trim() + ' …' : s;
    };

    const parseApiResponse = (maybe) => {
      if (!maybe) return null;
      if (typeof maybe === 'object') return maybe;
      if (typeof maybe === 'string') {
        try { return JSON.parse(maybe); } catch (e) { return null; }
      }
      return null;
    };

    const rawClaims = Array.isArray(data?.claims) ? data.claims : [];

    const mapped = rawClaims.map((item) => {
      const claimObj = item.claim ?? null;
      const factCheck = item.fact_check ?? null;
      const wikipedia = item.wikipedia ?? null;
      const newsArticles = Array.isArray(item.news_articles) ? item.news_articles : [];

      const userAi = item.user_ai_result ?? item.user_ai ?? null;
      const apiResp = parseApiResponse(userAi?.api_response) ?? userAi?.api_response_parsed ?? parseApiResponse(item.api_response) ?? null;

      const id = claimObj?.id ?? item.id ?? item.claim_id ?? null;
      const fullText = (claimObj && (claimObj.claim_text ?? claimObj.text)) ?? item.claim_text ?? item.text ?? '';
      const timestamp = claimObj?.created_at ?? item.created_at ?? item.timestamp ?? null;

      let textPreview = '';
      if (item.claim_summary) textPreview = String(item.claim_summary);
      else if (item.summary) textPreview = String(item.summary);
      else if (apiResp && (apiResp.summary || apiResp.claimSummary)) textPreview = String(apiResp.summary ?? apiResp.claimSummary);
      else textPreview = makePreview(fullText);

      const verdict = (apiResp?.verdict ?? factCheck?.result ?? item.verdict) ?? 'inconclusive';
      const confidence = apiResp?.confidence ?? factCheck?.confidence ?? null;

      return {
        id,
        text: textPreview || '(no text)',
        fullText: fullText || '',
        timestamp,
        factCheck,
        wikipedia,
        newsArticles,
        apiResp,
        verdict,
        confidence,
        raw: item
      };
    });

    return { claims: mapped, raw: data };
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('FetchUserData aborted due to timeout');
      throw new Error('User data request timed out');
    }
    console.error('Error fetching user data:', error);
    throw error;
  }
}
